// Supabase client cast to a permissive type — single, documented escape hatch.
//
// Why this exists:
//   `src/integrations/supabase/types.ts` is auto-generated from the database
//   schema, but a number of tables/functions are intentionally NOT regenerated
//   on every PR (e.g. RPCs added in migrations that have not yet been pushed
//   through the type generator). Touching call sites with `(supabase as any)`
//   was the previous pattern, scattered across the codebase and tripping the
//   ESLint `no-explicit-any` rule on every check (see manual test AITS-033).
//
// The single `eslint-disable` line below is the ONLY place in the codebase
// where the project consciously opts out of the generated types. Every call
// site that needs to read an un-typed table/RPC should import from here.
//
// When `types.ts` is regenerated and the missing surface lands in `Database`,
// migrate the call site away from this helper to the strict `supabase` client
// and the import becomes dead — we can then remove it.

import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "./client";

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- documented escape hatch, see header comment
type UntypedSupabase = SupabaseClient<any, "public", any>;

export const supabaseUntyped: UntypedSupabase = supabase as unknown as UntypedSupabase;

/**
 * Convenience cast for inline usage when the call site already imports
 * `supabase` and only one or two queries need the escape hatch.
 *
 * Prefer importing `supabaseUntyped` directly when most calls in a file are
 * un-typed.
 */
export function asUntyped(client: typeof supabase): UntypedSupabase {
  return client as unknown as UntypedSupabase;
}
